import React, { useState, useEffect } from 'react';
import { Plus, X } from 'lucide-react';
import { CheckoutAddon } from '../../types';
import { useSettings } from '../SettingsContext';
import { useCurrency } from '../CurrencyContext';
import AddonForm from './AddonForm';
import AddonItemRow from './AddonItemRow';
import { AddonTemplate } from './addonTemplates';

interface AddonManagerModalProps {
  onClose: () => void;
}

const AddonManagerModal: React.FC<AddonManagerModalProps> = ({ onClose }) => {
  const { settings, updateSettings } = useSettings();
  const { currencyCode } = useCurrency();
  const addons: CheckoutAddon[] = settings.checkoutConfig.addons || [];

  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [addonId, setAddonId] = useState('');
  const [isCustomId, setIsCustomId] = useState(false);
  const [price, setPrice] = useState<number | string>(1499);
  const [currency, setCurrency] = useState(currencyCode || 'INR');
  const [description, setDescription] = useState('');
  const [features, setFeatures] = useState<string[]>(['']);
  const [isEnabled, setIsEnabled] = useState(true);

  useEffect(() => {
    if (!editingId && !isCustomId && name) {
      const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
      setAddonId(slug ? `addon_${slug}` : '');
    }
  }, [name, editingId, isCustomId]);

  const resetForm = () => {
    setName('');
    setAddonId('');
    setIsCustomId(false);
    setPrice(1499);
    setCurrency(currencyCode || 'INR');
    setDescription('');
    setFeatures(['']);
    setIsEnabled(true);
    setIsAdding(false);
    setEditingId(null);
  };

  const handleStartEdit = (addon: CheckoutAddon) => {
    setEditingId(addon.id);
    setName(addon.name);
    setAddonId(addon.id);
    setIsCustomId(true);
    setPrice(addon.price);
    setCurrency(addon.currency || currencyCode || 'INR');
    setDescription(addon.description);
    setFeatures(addon.features && addon.features.length > 0 ? [...addon.features] : ['']);
    setIsEnabled(addon.isEnabled);
    setIsAdding(true);
  };

  const saveUpdatedAddons = (updated: CheckoutAddon[]) => {
    updateSettings({ checkoutConfig: { ...settings.checkoutConfig, addons: updated } });
  };

  const handleSelectTemplate = (tpl: AddonTemplate) => {
    setName(tpl.name);
    setPrice(tpl.price);
    if (tpl.currency) setCurrency(tpl.currency);
    setDescription(tpl.desc);
    setFeatures([...tpl.features]);
    setIsCustomId(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    const finalId = addonId.trim() || `addon_${Date.now()}`;
    const cleanFeatures = features.map(f => f.trim()).filter(Boolean);

    const cleanAddon: CheckoutAddon = {
      id: finalId,
      name: name.trim(),
      price: Math.max(0, Number(price) || 0),
      currency: currency || 'INR',
      description: description.trim() || (cleanFeatures[0] || 'Premium workspace add-on'),
      features: cleanFeatures,
      isEnabled,
    };

    if (editingId) {
      saveUpdatedAddons(addons.map(a => (a.id === editingId ? cleanAddon : a)));
    } else {
      saveUpdatedAddons([...addons, cleanAddon]);
    }
    resetForm();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-800 rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95">
        <div className="p-6 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center bg-white dark:bg-slate-800">
          <div>
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300">
              Workspace & Checkout Add-ons
            </span>
            <h2 className="text-2xl font-black text-slate-900 dark:text-white mt-1">Manage Add-ons</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="flex justify-between items-center">
            <p className="text-xs text-slate-500">Configure add-ons with custom currency and multiple features.</p>
            {!isAdding && (
              <button
                onClick={() => {
                  resetForm();
                  setIsAdding(true);
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 flex items-center gap-1.5 shadow-sm shrink-0"
              >
                <Plus className="w-3.5 h-3.5" /> New Add-on
              </button>
            )}
          </div>

          {isAdding && (
            <AddonForm
              name={name}
              setName={setName}
              addonId={addonId}
              setAddonId={setAddonId}
              setIsCustomId={setIsCustomId}
              price={price}
              setPrice={setPrice}
              currency={currency}
              setCurrency={setCurrency}
              description={description}
              setDescription={setDescription}
              features={features}
              onAddFeature={(t = '') => setFeatures(prev => [...prev, t])}
              onUpdateFeature={(idx, val) => setFeatures(prev => { const n = [...prev]; n[idx] = val; return n; })}
              onRemoveFeature={(idx) => setFeatures(prev => prev.filter((_, i) => i !== idx))}
              onSelectTemplate={handleSelectTemplate}
              editingId={editingId}
              onSubmit={handleSubmit}
              onCancel={resetForm}
            />
          )}

          <div className="space-y-3">
            {addons.map(addon => (
              <AddonItemRow
                key={addon.id}
                addon={addon}
                onEdit={handleStartEdit}
                onToggle={id => saveUpdatedAddons(addons.map(a => a.id === id ? { ...a, isEnabled: !a.isEnabled } : a))}
                onDelete={id => saveUpdatedAddons(addons.filter(a => a.id !== id))}
              />
            ))}

            {addons.length === 0 && !isAdding && (
              <div className="text-center py-10 bg-slate-50 dark:bg-slate-900 rounded-3xl border border-dashed border-slate-200 dark:border-slate-700">
                <p className="text-xs text-slate-500 font-medium">No add-ons created yet.</p>
                <button
                  onClick={() => setIsAdding(true)}
                  className="mt-2 text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  + Create your first add-on
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddonManagerModal;
